import { useCallback, useEffect, useState } from 'react';
import type { AlbumDetail, AlbumItemView, ReconcileSummary } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { go } from '../lib/route';
import { FramePicker } from '../components/FramePicker';
import { LibraryPicker } from '../components/LibraryPicker';
import { PhotoTextForm } from '../components/PhotoText';
import { Shell } from '../components/Shell';
import { UploadPanel } from '../components/UploadPanel';

interface Props {
  readonly session: StoredSession;
  readonly albumId: string;
  readonly onSignOut: () => void;
}

/**
 * What a change actually did, frame by frame.
 *
 * Worth the words: "added" and "waiting for approval" are different outcomes on
 * different frames from the same tap, and an album owner who is told only
 * "saved" has no way to know that half of it is sitting in somebody's queue.
 */
const describePush = (pushed: readonly ReconcileSummary[]): string | null => {
  const said: string[] = [];
  for (const frame of pushed) {
    if (frame.blocked) {
      said.push(`${frame.deviceLabel}: you can no longer send there`);
      continue;
    }
    const parts: string[] = [];
    if (frame.added) parts.push(`${frame.added} up`);
    if (frame.queued) parts.push(`${frame.queued} waiting for approval`);
    if (frame.removed) parts.push(`${frame.removed} taken down`);
    if (parts.length > 0) said.push(`${frame.deviceLabel}: ${parts.join(', ')}`);
  }
  return said.length > 0 ? said.join(' · ') : null;
};

export const Album = ({ session, albumId, onSignOut }: Props) => {
  const [album, setAlbum] = useState<AlbumDetail | null>(null);
  const [items, setItems] = useState<readonly AlbumItemView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pushed, setPushed] = useState<string | null>(null);
  /** Which item has its details open. */
  const [editing, setEditing] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [renaming, setRenaming] = useState('');
  const [adding, setAdding] = useState<readonly string[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);

  const reload = useCallback(async () => {
    try {
      const result = await api.getAlbum(session.token, albumId);
      setAlbum(result.album);
      setItems(result.items);
      setRenaming(result.album.label);
    } catch (caught) {
      setMissing(true);
      setError(caught instanceof Error ? caught.message : 'That album is not available.');
    }
  }, [albumId, session.token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Every mutation goes through here so the reconcile report is never dropped. */
  const act = async (run: () => Promise<{ pushed: readonly ReconcileSummary[] } | void>) => {
    setError(null);
    try {
      const result = await run();
      setPushed(result ? describePush(result.pushed) : null);
      await reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work.');
    }
  };

  if (missing) {
    return (
      <Shell who={session.account.name} onSignOut={onSignOut} back={{ label: 'Albums', to: '/a' }}>
        <p className="alert" style={{ marginTop: '2rem' }}>
          {error ?? 'That album is not available.'}
        </p>
      </Shell>
    );
  }

  if (!album) {
    return (
      <Shell who={session.account.name} onSignOut={onSignOut}>
        <p className="eyebrow" style={{ marginTop: '2rem' }}>
          Loading
        </p>
      </Shell>
    );
  }

  const taken = items.map((item) => item.assetId);
  const showingOn = album.publications.map((entry) => entry.deviceId);

  return (
    <Shell
      title={album.label}
      who={session.account.name}
      onSignOut={onSignOut}
      back={{ label: 'Albums', to: '/a' }}
    >
      <div className="stack-loose" style={{ marginTop: '1.5rem' }}>
        {error && <p className="alert">{error}</p>}
        {pushed && (
          <p className="alert" data-tone="ok">
            {pushed}
          </p>
        )}

        {/* ------------------------------------------------------ showing on -- */}

        <section>
          <div className="section-head">
            <p className="eyebrow">Showing on</p>
          </div>

          {album.publications.length === 0 ? (
            <p className="note">
              Not on a frame yet. Choose one below and everything in this album goes up — and stays
              up to date.
            </p>
          ) : (
            <ul className="ledger">
              {album.publications.map((entry) => (
                <li key={entry.deviceId}>
                  <span className="ledger-main">
                    <span className="ledger-name">{entry.deviceLabel}</span>
                    <span className="ledger-meta">
                      {!entry.canSend
                        ? 'you can no longer send here — nothing new is going up'
                        : entry.autoAccept
                          ? 'new photos go straight up'
                          : 'new photos wait for approval'}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="button button-quiet button-small"
                    onClick={() =>
                      void act(async () => {
                        await api.unpublishAlbum(session.token, album.id, entry.deviceId);
                      })
                    }
                  >
                    Take it off
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="stack" style={{ marginTop: '1.25rem' }}>
            <p className="eyebrow">Put it on another frame</p>
            <FramePicker
              session={session}
              selected={adding}
              onChange={setAdding}
              exclude={showingOn}
              emptyText="This album is on every frame you can send to."
            />
            {adding.length > 0 && (
              <p>
                <button
                  type="button"
                  className="button button-small"
                  onClick={() =>
                    void act(async () => {
                      const reports: ReconcileSummary[] = [];
                      for (const deviceId of adding) {
                        const result = await api.publishAlbum(session.token, album.id, deviceId);
                        if (result.published) reports.push(result.published);
                      }
                      setAdding([]);
                      return { pushed: reports };
                    })
                  }
                >
                  Put it on {adding.length} frame{adding.length === 1 ? '' : 's'}
                </button>
              </p>
            )}
          </div>
        </section>

        {/* ----------------------------------------------------------- items -- */}

        <section>
          <div className="section-head">
            <p className="eyebrow">
              In this album · {items.length} photo{items.length === 1 ? '' : 's'}
            </p>
          </div>

          {items.length === 0 ? (
            <p className="empty">Nothing in here yet. Add photos below.</p>
          ) : (
            <ul className="sheet">
              {items.map((item, index) => (
                <li key={item.assetId} className="print" data-state="static">
                  <div className="print-image">
                    <img
                      src={item.thumbUrl}
                      alt={item.caption ?? `Photo ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <p className="print-caption">
                    <span className="print-frame">{String(index + 1).padStart(3, '0')}</span>
                    <span className="print-status">{item.location ?? ''}</span>
                  </p>

                  {item.caption && <p className="note">{item.caption}</p>}

                  {editing === item.assetId ? (
                    <PhotoTextForm
                      value={{ caption: item.caption, location: item.location }}
                      reach={
                        album.publications.length === 0
                          ? 'This is the album’s own text, and travels with the photo wherever the album is published.'
                          : `This changes the photo on ${album.publications.length} frame${
                              album.publications.length === 1 ? '' : 's'
                            }.`
                      }
                      onCancel={() => setEditing(null)}
                      onSave={async (text) => {
                        const result = await api.editAlbumItem(
                          session.token,
                          album.id,
                          item.assetId,
                          text,
                        );
                        setItems((current) =>
                          current.map((entry) =>
                            entry.assetId === item.assetId ? { ...entry, ...text } : entry,
                          ),
                        );
                        setPushed(describePush(result.pushed));
                        setEditing(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      onClick={() => setEditing(item.assetId)}
                    >
                      {item.caption || item.location ? 'Edit details' : 'Add details'}
                    </button>
                  )}

                  <button
                    type="button"
                    className="print-remove"
                    aria-label={`Take photo ${index + 1} out of this album`}
                    onClick={() =>
                      void act(() =>
                        api.removeFromAlbum(session.token, album.id, item.assetId),
                      )
                    }
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ------------------------------------------------------ adding to -- */}

        <section className="stack-loose">
          <div className="section-head">
            <p className="eyebrow">Add photos</p>
            <button
              type="button"
              className="button button-quiet button-small"
              onClick={() => setShowLibrary((now) => !now)}
            >
              {showLibrary ? 'Hide your photos' : 'Choose from your photos'}
            </button>
          </div>

          {showLibrary && (
            <LibraryPicker
              session={session}
              taken={taken}
              onAdd={(assetIds) =>
                act(() => api.addToAlbum(session.token, album.id, assetIds))
              }
            />
          )}

          <UploadPanel
            doneText={album.publications.length === 0 ? 'In the album' : 'On the frames'}
            upload={async (photo, text) => {
              const result = await api.uploadToAlbum(session.token, album.id, photo, text);
              setPushed(describePush(result.pushed));
              // The panel speaks in upload terms, so translate: an album photo
              // is "accepted" when it is not sitting in anybody's queue, which
              // includes an album that is not on a frame at all yet.
              return {
                id: result.assetId,
                duplicate: result.duplicate,
                accepted:
                  result.pushed.length === 0 ||
                  result.pushed.some((frame) => frame.added > 0) ||
                  result.pushed.every((frame) => frame.queued === 0),
              };
            }}
            onSettled={reload}
          />
        </section>

        {/* --------------------------------------------------------- album -- */}

        <section className="stack">
          <div className="section-head">
            <p className="eyebrow">This album</p>
          </div>
          <div className="row">
            <label className="field">
              <span>Name</span>
              <input
                className="input"
                value={renaming}
                maxLength={40}
                onChange={(event) => setRenaming(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="button button-quiet"
              disabled={renaming.trim() === album.label || renaming.trim() === ''}
              onClick={() =>
                void act(async () => {
                  await api.renameAlbum(session.token, album.id, renaming.trim());
                })
              }
            >
              Rename
            </button>
          </div>

          <p className="note">
            Deleting an album takes its photos off every frame showing it. The pictures stay in
            your library.
          </p>
          <p>
            <button
              type="button"
              className="button button-danger button-small"
              // Not routed through `act`: that reloads the album afterwards, and
              // there is nothing left to reload.
              onClick={() => {
                setError(null);
                api
                  .deleteAlbum(session.token, album.id)
                  .then(() => go('/a'))
                  .catch((caught: unknown) =>
                    setError(
                      caught instanceof Error ? caught.message : 'Could not delete that album.',
                    ),
                  );
              }}
            >
              Delete this album
            </button>
          </p>
        </section>
      </div>
    </Shell>
  );
};
